import { SideEffectManager } from "side-effect-manager";
import { IService } from "../typing";
import { Remitter } from "remitter";
import { IServiceVideoChatEventData } from "./events";

export type IServiceVideoChatUID = string;

export interface IServiceVideoChatDevice {
    deviceId: string;
    label: string;
}

export interface IServiceVideoChatAvatar {
    enableCamera(enabled: boolean): void;
    enableMic(enabled: boolean): void;
    setElement(element: HTMLElement | null): void;
    updateElementView(element: HTMLElement | null): void;
    getVolumeLevel(): number;
    destroy(): void;
    enableRemoteAudio: (enabled: boolean) => void;
    refreshAvatar: () => void;
    hasEl: () => boolean;
}

export interface IServiceVideoChatJoinRoomConfig {
    uid: IServiceVideoChatUID;
    roomUUID: string;
    userSig: string;
    role?: "Audience" | "Anchor";
    isTurnListenOn?: boolean;
    /**
     * 是否开启回声消除
     */
    isEnableAES?: boolean;
    /**
     * 是否开启自动增益
     */
    isEnableAGC?: boolean;
}

export abstract class IServiceVideoChat implements IService {
    protected readonly sideEffect = new SideEffectManager();

    public readonly events = new Remitter<IServiceVideoChatEventData>();

    public abstract readonly isJoinedRoom: boolean;

    public async destroy(): Promise<void> {
        this.sideEffect.flushAll();
        this.events.destroy();
    }

    // 加入房间与退出房间
    public abstract joinRoom(config: IServiceVideoChatJoinRoomConfig): Promise<void>;
    public abstract leaveRoom(): Promise<void>;

    // 获取Avatar实例, 当有多个用户时, 每个用户可以通过uid来获取对应的Avatar实例
    public abstract getAvatar(uid: IServiceVideoChatUID): IServiceVideoChatAvatar | undefined;

    // 获取音量, 当有多个用户时, 每个用户可以通过uid来获取对应的音量
    public abstract getVolumeLevel(uid?: IServiceVideoChatUID): number;

    // 设置/获取摄像头设备
    public abstract setCameraID(deviceId: string): Promise<void>;
    public abstract getCameraID(): string | undefined;

    // 设置/获取麦克风设备
    public abstract setMicID(deviceId: string): Promise<void>;
    public abstract getMicID(): string | undefined;

    // 设置/获取扬声器设备
    public abstract setSpeakerID(deviceId: string): Promise<void>;
    public abstract getSpeakerID(): string | undefined;

    // 获取上述设备的列表
    public abstract getCameraDevices(): Promise<IServiceVideoChatDevice[]>;
    public abstract getMicDevices(): Promise<IServiceVideoChatDevice[]>;
    public abstract getSpeakerDevices(): Promise<IServiceVideoChatDevice[]>;

    // 获取扬声器的音量, 测试扬声器设备时可用, 范围0~100
    public abstract getSpeakerVolume(): number;
    public abstract getMicVolume(): number;
    public abstract setMicVolume(_volume: number): void;

    // 设置是否获取全部远端流
    public abstract setIsMuteAllRemoteStream(_enabled: boolean): void;
    public abstract setIsMuteAllRemoteAudio(_enabled: boolean): void;

    // 设置音频的额外处理
    public abstract setAudioAEC(_enabled: boolean): void; // 音频回声消除
    public abstract setAudioAGC(_enabled: boolean): void; // 音频自动增益

    // 设置扬声器的音量, 测试扬声器设备时可用, 范围0~100
    public setSpeakerVolume(_volume: number): void {
        throw doesNotSupportError("setting speaker volume");
    }
    // 网络状态测试
    public startNetworkTest(): void {
        throw doesNotSupportError("network probe test");
    }

    // 停止网络状态测试
    public stopNetworkTest(): void {
        throw doesNotSupportError("network probe test");
    }

    // 摄像头预览/测试
    public startCameraTest(_el: HTMLElement): void {
        throw doesNotSupportError("camera test");
    }

    // 停止摄像头预览/测试
    public stopCameraTest(): void {
        throw doesNotSupportError("camera test");
    }

    // 麦克风测试
    public startMicTest(): void {
        throw doesNotSupportError("microphone test");
    }

    // 停止麦克风测试
    public stopMicTest(): void {
        throw doesNotSupportError("microphone test");
    }

    // 扬声器测试
    public startSpeakerTest(_filePath: string): void {
        throw doesNotSupportError("speaker test");
    }

    // 停止扬声器测试
    public stopSpeakerTest(): void {
        throw doesNotSupportError("speaker test");
    }

    // 是否开启视频镜像
    public setVideoMirror(_enabled: boolean): void {
        throw doesNotSupportError("video mirror");
    }

    // 设置playout音量
    public setPlayoutVolume(_volume: number): void {
        throw doesNotSupportError("set playout volume");
    }

    // 设置capture音量
    public setCaptureVolume(_volume: number): void {
        throw doesNotSupportError("set capture volume");
    }
}

function doesNotSupportError(type: string): Error {
    return new Error(`Does not support ${type}`);
}
